import React from "react";
import { useSOC } from "../context/SOCContext";
import { DataTable } from "../../common/DataTable";
import { StatusBadge } from "../ui/Badges";
import KpiCard from "../ui/KpiCard";
import ApiLoader from "../../ui/ApiLoader";
import { GlobeAltIcon, FingerPrintIcon, UserGroupIcon, TagIcon } from "@heroicons/react/24/outline";

const ThreatIntel = () => {
  const { threats, loading, errors } = useSOC();

  const maliciousIps = threats.filter((t) => t.type === "IP").length;
  const maliciousDomains = threats.filter((t) => t.type === "Domain").length;
  const fileHashes = threats.filter((t) => t.type === "File Hash").length;
  const actors = new Set(threats.map((t) => t.actor)).size;

  const uniqueSources = [...new Set(threats.map((t) => t.source).filter(Boolean))];

  const columns = [
    {
      id: "ioc",
      accessorFn: (r) => r.ioc,
      header: "IOC",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "IOC", placeholder: "Search IOC, threat, actor" } },
      cell: ({ row }) => <span>{row.original.ioc}</span>,
    },
    {
      id: "type",
      accessorFn: (r) => r.type,
      header: "Type",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Type",
          placeholder: "All types",
          options: ["IP", "Domain", "File Hash"].map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.type}</span>,
    },
    {
      id: "threat",
      accessorFn: (r) => r.threat,
      header: "Threat",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Threat", placeholder: "Search threat" } },
      cell: ({ row }) => <span>{row.original.threat}</span>,
    },
    {
      id: "confidence",
      accessorFn: (r) => r.confidence,
      header: "Confidence",
      enableSorting: false,
      meta: { filter: { variant: "number", label: "Confidence", placeholder: "Confidence %" } },
      cell: ({ row }) => <span>{row.original.confidence}%</span>,
    },
    {
      id: "firstSeen",
      accessorFn: (r) => r.firstSeen,
      header: "First Seen",
      enableSorting: false,
      cell: ({ row }) => <span>{new Date(row.original.firstSeen).toLocaleDateString()}</span>,
    },
    {
      id: "lastSeen",
      accessorFn: (r) => r.lastSeen,
      header: "Last Seen",
      enableSorting: false,
      cell: ({ row }) => <span>{new Date(row.original.lastSeen).toLocaleDateString()}</span>,
    },
    {
      id: "source",
      accessorFn: (r) => r.source,
      header: "Source",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Source",
          placeholder: "All sources",
          options: uniqueSources.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.source}</span>,
    },
    {
      id: "status",
      accessorFn: (r) => r.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: ["Active", "Monitoring", "Expired"].map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
  ];

  if (loading.threats && threats.length === 0) {
    return <ApiLoader variant="spinner" label="Loading Threat Intelligence..." className="py-20" />;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Malicious IPs" value={maliciousIps} icon={GlobeAltIcon} tone="red" />
        <KpiCard label="Malicious Domains" value={maliciousDomains} icon={TagIcon} tone="orange" />
        <KpiCard label="File Hashes" value={fileHashes} icon={FingerPrintIcon} tone="purple" />
        <KpiCard label="Threat Actors Tracked" value={actors} icon={UserGroupIcon} tone="cyan" />
      </div>

      <DataTable
        columns={columns}
        data={threats}
        getRowId={(r) => String(r.ioc)}
        emptyMessage={errors.threats || "No records found"}
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        maxBodyHeight="65vh"
      />
    </div>
  );
};

export default ThreatIntel;
